import type { PlayersSortByType } from "@repo/shared/api";

import { MetaRatingsPlayer } from "../../../redux/players/playersSlice";
import axios from "../../api";

const queryParamDict = {
  countries: "country",
  leagues: "league",
  clubs: "club",
  skillMoves: "skill-moves",
  weakFoot: "weak-foot",
  positions: "position",
  rarities: "rarity",
  maxPlayStylesMax: "maxPlayStyles",
  maxPlayStylesPlusMax: "maxPlayStylesPlus",
  maxPlayStylesMin: "minPlayStyles",
  maxPlayStylesPlusMin: "minPlayStylesPlus",
} as const;

type BackendMetaRatingsPlayer = MetaRatingsPlayer & {
  positions: string[];
  isSbc: boolean;
  isObjective: boolean;
};

export const getPlayers = async ({
  page,
  sort,
  myClub,
  myEvolvedPlayers,
  activeSquad,
  chainedEvo,
  excludePaidEvo,
  excludeCompletedEvolutions,
  playerRoleId,
  playerRoleIds,
  queryParams,
  evolutions,
  evoIds,
  mustIncludeEvoIds,
  searchText,
  playStylesLab,
  favoriteClubsAndCountries,
}: {
  page: number;
  sort: PlayersSortByType;
  myClub?: boolean;
  myEvolvedPlayers?: boolean;
  activeSquad?: boolean;
  chainedEvo?: boolean;
  excludePaidEvo?: boolean;
  excludeCompletedEvolutions?: boolean;
  playerRoleId?: number;
  playerRoleIds?: number[];
  queryParams?: any;
  evolutions?: boolean;
  evoIds?: number[];
  mustIncludeEvoIds?: number[];
  searchText?: string;
  playStylesLab?: boolean;
  /*
    When set, the backend matches players whose club OR nation is one of the
    signed-in user's saved favorites (favoriteClubs / favoriteCountries). Sends
    a bare flag — the IDs come from the user record server-side, not the query.
  */
  favoriteClubsAndCountries?: boolean;
}): Promise<{
  players: MetaRatingsPlayer[];
  totalPages: number;
}> => {
  const params: string[] = [];

  // Add page parameter
  params.push(`page=${page}`);

  // Add sort parameter without value

  params.push(`sort-${sort}`);

  // Add boolean parameters without values
  if (myClub) params.push("my-club");
  if (myEvolvedPlayers) params.push("my-evolved");
  if (activeSquad) params.push("active-squad");
  if (chainedEvo) params.push("chained");
  if (excludePaidEvo) params.push("excludePaid");
  if (excludeCompletedEvolutions) params.push("excludeCompleted");
  if (playStylesLab) params.push("playstyles-lab");
  if (favoriteClubsAndCountries) params.push("favorite-clubs-and-countries");

  // Add parameters with values
  if (playerRoleIds?.length) {
    params.push(`player-role-ids=${playerRoleIds.join(",")}`);
  } else if (playerRoleId) {
    params.push(`player-role-id=${playerRoleId}`);
  }
  if (evoIds?.length) params.push(`included-evolutions=${evoIds.join(",")}`);
  if (mustIncludeEvoIds?.length)
    params.push(`must-include-evolutions=${mustIncludeEvoIds.join(",")}`);
  if (searchText) params.push(`search=${searchText}`);

  queryParams?.forEach((element) => {
    const elementAsObject = Object.entries(element);
    const key = elementAsObject[0][0];
    const values = elementAsObject[0][1] as number[];
    if (values.length > 0) {
      params.push(`${queryParamDict[key] || key}=${values.join(",")}`);
    }
  });

  const queryString = params.join("&");

  const res = await axios.get<{
    players: BackendMetaRatingsPlayer[];
    totalPages: number;
  }>(`/players${evolutions ? "/evolutions" : ""}?v2&${queryString}`);

  const { players, totalPages } = res.data;

  const playersRes = players.map((player) => ({
    ...player,
    positions: Array.from(
      new Set([player.preferredPosition, ...player.possiblePositions]),
    ),
    isSbcPlayer: player.isSbc,
    isObjectivePlayer: player.isObjective,
  }));

  return { players: playersRes, totalPages };
};
